import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import s from "./Testing.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { ThresholdsForm } from "../entryForms/ThresholdsForm";
import { useTestingActivity, useTestingSummary, useTestRecords, useTestTypes } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  attentionList,
  describeLog,
  NO_PROVIDER,
  providersOf,
  DUE_LABEL,
  SOURCE_LABEL,
  type LogEntry,
  type TestRecord,
  type TestType,
  type TypeSummary,
  frameworksOf,
  matchesRecord,
  OUTCOME_LABEL,
  OUTCOMES,
  recordsCsv,
  type Outcome,
  type RecordFilters,
} from "../../domain/testing";
import { isoDay } from "../../domain/workOrderReport";
import { Button, Card, EmptyState, ErrorState, Input, Select, Skeleton, Table } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { downloadText } from "../work-orders/WorkOrdersPage";
import { OutcomeText } from "./panels";
import { RecordTestDialog } from "./RecordTestDialog";
import { Drawer, Fact, drawerStyles as ds } from "./Drawer";
import { t } from "../../i18n";

/**
 * Every mandatory test at the site: what needs attention now, where each type
 * of test stands against its interval, and the searchable register of every
 * test recorded, with its verdict and its files.
 */
export function TestingRecordsPage() {
  const { site, principal, tenant, serverTime } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const types = useTestTypes();
  const records = useTestRecords();
  const summary = useTestingSummary();
  const [recording, setRecording] = useState(false);
  const [openType, setOpenType] = useState<string | null>(null);
  const activity = useTestingActivity();
  const reporting = params.get("view") === "report";
  const canWrite = hasPermission(principal, PERMISSION.testsWrite);

  const f: RecordFilters = {
    q: params.get("q") ?? "",
    testType: params.get("type"),
    outcome: (params.get("outcome") as Outcome | null) || null,
    framework: params.get("framework"),
    from: params.get("from") ?? undefined,
    to: params.get("to") ?? undefined,
    demo: (params.get("demo") as RecordFilters["demo"]) || "all",
    provider: params.get("provider"),
  };
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const rows = useMemo(
    () => (records.data ?? []).filter((r) => matchesRecord(r, f)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records.data, params.toString()],
  );
  const attention = useMemo(() => (summary.data ? attentionList(summary.data) : []), [summary.data]);
  const frameworks = useMemo(() => frameworksOf(types.data ?? []), [types.data]);
  const demoCount = (records.data ?? []).filter((r) => r.demo).length;
  /* who has actually carried tests out here, and how many name nobody */
  const providers = useMemo(() => providersOf(records.data ?? []), [records.data]);

  return (
    <Page
      eyebrow={
        reporting
          ? t("{site} · assets & engineering · report", { site: site.code })
          : t("{site} · assets & engineering", { site: site.code })
      }
      title={reporting ? "Testing records — report" : "Testing records"}
      actions={
        <div className={`${s.filters} ${s.noPrint}`}>
          {reporting ? (
            <>
              <Button variant="primary" onClick={() => window.print()}>
                {t("Print / save as PDF")}
              </Button>
              <Button onClick={() => set("view", null)}>{t("Back")}</Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                onClick={() => setRecording(true)}
                disabled={!canWrite || !types.data?.length}
                title={canWrite ? undefined : t("Requires {permission}", { permission: PERMISSION.testsWrite })}
              >
                {t("+ Record a test")}
              </Button>
              <Button onClick={() => set("view", "report")}>{t("Generate report")}</Button>
            </>
          )}
          <Button
            onClick={() =>
              downloadText(
                `testing-records-${site.code}-${isoDay(new Date())}.csv`,
                recordsCsv(rows),
                "text/csv;charset=utf-8",
              )
            }
            disabled={!rows.length}
          >
            {t("Export CSV")}
          </Button>
        </div>
      }
    >
      <div className={s.page} data-testid="testing-records">
        {reporting && (
          <div className={s.note}>
            {t("{site} · {name} · generated {date} by {person}", {
              site: site.code,
              name: site.name,
              date: formatDateTime(serverTime),
              person: principal.name,
            })}
            {tenant ? ` · ${tenant}` : ""}
          </div>
        )}
        {demoCount > 0 && (
          <div className={s.note} role="status">
            <span className={`${s.badge} ${s.demo}`}>{t("DEMONSTRATION DATA")}</span>{" "}
            {t("{n} of these records are demonstration data, marked on each row. Remove them with", {
              n: demoCount,
            })}{" "}
            <span className="mono">scripts/seed_demo_tests.py --remove</span>.
          </div>
        )}

        {summary.isPending ? (
          <Skeleton rows={4} />
        ) : summary.isError ? (
          <ErrorState error={summary.error} retry={() => void summary.refetch()} />
        ) : summary.data.types.length === 0 ? (
          <EmptyState>
            No test types are declared for this tenant, so there is nothing to record against yet.
          </EmptyState>
        ) : (
          <>
            <div className={s.tiles}>
              {(["fail", "attention", "pass"] as const).map((o) => (
                <button
                  key={o}
                  type="button"
                  className={`${s.tile} ${s.tileButton}`}
                  aria-pressed={f.outcome === o}
                  title={t("Show the records whose verdict is {outcome}", {
                    outcome: t(OUTCOME_LABEL[o]).toLowerCase(),
                  })}
                  onClick={() => {
                    set("outcome", f.outcome === o ? null : o);
                    document.getElementById("test-register")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  <span className={s.tileLabel}>
                    {t("Latest verdict: {outcome}", { outcome: t(OUTCOME_LABEL[o]) })}
                  </span>
                  <span className={`${s.tileValue} ${summary.data.latestOutcomes[o] && o !== "pass" ? s[o] : ""}`}>
                    {summary.data.latestOutcomes[o]}
                  </span>
                  <span className={s.tileNote}>{t("test types whose last test was this — click to list")}</span>
                </button>
              ))}
              <button
                type="button"
                className={`${s.tile} ${s.tileButton}`}
                title={t("Show the mandatory tests table")}
                onClick={() =>
                  document.getElementById("mandatory-tests")?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                <span className={s.tileLabel}>{t("Overdue")}</span>
                <span className={`${s.tileValue} ${summary.data.overdue ? s.fail : ""}`}>{summary.data.overdue}</span>
                <span className={s.tileNote}>{t("{n} due within 30 days", { n: summary.data.dueSoon })}</span>
              </button>
              <button
                type="button"
                className={`${s.tile} ${s.tileButton}`}
                title={t("Show the tests never done")}
                onClick={() =>
                  document.getElementById("mandatory-tests")?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
              >
                <span className={s.tileLabel}>{t("Never tested")}</span>
                <span className={s.tileValue}>{summary.data.neverTested}</span>
                <span className={s.tileNote}>{t("of {n} mandatory test types", { n: summary.data.types.length })}</span>
              </button>
              <button
                type="button"
                className={`${s.tile} ${s.tileButton}`}
                title={t("Clear the filters and show every record")}
                onClick={() => {
                  setParams(new URLSearchParams(), { replace: true });
                  document.getElementById("test-register")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                <span className={s.tileLabel}>{t("Records")}</span>
                <span className={s.tileValue}>{records.data?.length ?? "…"}</span>
                <span className={s.tileNote}>
                  {t("{files} files · {actions} actions logged", {
                    files: (records.data ?? []).reduce((a, r) => a + r.files, 0),
                    actions: (records.data ?? []).reduce((a, r) => a + r.actions, 0),
                  })}
                </span>
              </button>
            </div>

            <Card
              title="Needs attention or action"
              sub="Failed, late or never-done tests"
            >
              {attention.length === 0 ? (
                <EmptyState>Every test type's latest test passed and none is overdue.</EmptyState>
              ) : (
                <ul className={s.attentionList}>
                  {attention.map((a, i) => (
                    <li key={`${a.testType}.${a.severity}.${i}`} className={s.attentionItem} data-severity={a.severity}>
                      <div>
                        <b
                          className={
                            a.severity === "fail" ? s.fail : a.severity === "attention" ? s.attention : undefined
                          }
                        >
                          {a.severity === "fail"
                            ? t("Action needed")
                            : a.severity === "attention"
                              ? t("Needs attention")
                              : t("Test due")}
                        </b>{" "}
                        —{" "}
                        {a.recordId && !reporting ? (
                          <Link to={`/testing-records/${a.recordId}`}>{a.name}</Link>
                        ) : (
                          a.name
                        )}
                      </div>
                      <div className={s.note}>{a.why}</div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <div id="mandatory-tests" />
            <Card
              title="Mandatory tests"
              sub="Each test, its source, last result and next due"
            >
              <div style={{ overflowX: "auto" }}>
                <Table>
                  <thead>
                    <tr>
                      <th>{t("Test")}</th>
                      <th>{t("Last test")}</th>
                      <th>{t("Verdict")}</th>
                      <th>{t("Next due")}</th>
                      <th>{t("Interval")}</th>
                      <th>{t("Judged against")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.data.types.map((ty) => {
                      const type = types.data?.find((x) => x.key === ty.testType);
                      return (
                        <tr
                          key={ty.testType}
                          className={reporting ? undefined : s.clickRow}
                          tabIndex={reporting ? undefined : 0}
                          onClick={(e) => {
                            if (reporting || (e.target as HTMLElement).closest("a")) return;
                            setOpenType(ty.testType);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && !reporting && setOpenType(ty.testType)}
                        >
                          <td>
                            <b>{ty.name}</b>
                            <div className={s.frameworks}>{ty.frameworks.join(" · ")}</div>
                          </td>
                          <td>
                            {ty.last ? (
                              reporting ? (
                                `${ty.last.reference} · ${formatDate(ty.last.performedOn)}`
                              ) : (
                                <Link to={`/testing-records/${ty.last.id}`}>
                                  {ty.last.reference} · {formatDate(ty.last.performedOn)}
                                </Link>
                              )
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>
                            {ty.last ? (
                              <OutcomeText outcome={ty.last.outcome} />
                            ) : (
                              <span className={s.not_assessed}>—</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={
                                ty.state === "overdue" ? s.fail : ty.state === "due_soon" ? s.attention : undefined
                              }
                            >
                              {ty.nextDue ? formatDate(ty.nextDue) : "—"}
                            </span>
                            <div className={s.tileNote}>{t(DUE_LABEL[ty.state])}</div>
                          </td>
                          <td>
                            {ty.intervalDays ? t("{n} d", { n: ty.intervalDays }) : "—"}
                            {ty.intervalDays && ty.intervalStatus !== "agreed" && (
                              <div className={s.tileNote}>{t("proposed")}</div>
                            )}
                          </td>
                          <td className={s.note}>
                            {type?.mode === "judged" && t("Engineer's verdict")}
                            {type?.mode === "ratio" &&
                              type.ratio &&
                              `${type.ratio.valueLabel} ≥ ${type.ratio.limitLabel}`}
                            {type?.mode === "threshold" &&
                              type.thresholds.map((th) => (
                                <div key={th.label} title={th.basisNote}>
                                  {t("{label}: attention {attention}, action {fail}", {
                                    label: th.label,
                                    attention: th.attention ?? "—",
                                    fail: th.fail ?? "—",
                                  })}
                                  {th.basis === "pct_of_specified"
                                    ? ` ${t("% of specified")}`
                                    : type.unit
                                      ? ` ${type.unit}`
                                      : ""}
                                </div>
                              ))}
                            {type &&
                              (type.thresholds.some((th) => th.status !== "agreed") ||
                                (type.ratio && type.ratio.status !== "agreed")) && (
                                <span
                                  className={`${s.badge} ${s.proposed}`}
                                  title={type.thresholds[0]?.basisNote ?? type.ratio?.basisNote}
                                >
                                  {t("PROPOSED")}
                                </span>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <p className={s.note}>
                {t(
                  "PROPOSED thresholds and intervals are reference values (source on hover), not this airport's certified figures. Anyone with",
                )}{" "}
                <span className="mono">rule:verify</span>{" "}
                {t("confirms them. Records already judged keep their original thresholds.")}
              </p>
            </Card>
          </>
        )}

        <div id="test-register" />
        <Card
          title="Test register"
          sub="Every test at this site, newest first"
        >
          {!reporting && (
            <div className={s.filters} style={{ marginBottom: 10 }}>
              <Input
                type="search"
                placeholder={t("Search reference, title, provider, scope, method, device, engineering note, finding…")}
                value={f.q}
                onChange={(e) => set("q", e.target.value || null)}
                aria-label={t("Search tests")}
              />
              <Select
                value={f.testType ?? ""}
                onChange={(e) => set("type", e.target.value || null)}
                aria-label={t("Test type")}
              >
                <option value="">{t("Any test")}</option>
                {(types.data ?? []).map((ty) => (
                  <option key={ty.key} value={ty.key}>
                    {ty.name}
                  </option>
                ))}
              </Select>
              <Select
                value={f.provider ?? ""}
                onChange={(e) => set("provider", e.target.value || null)}
                aria-label={t("Service provider")}
              >
                <option value="">{t("Any service provider")}</option>
                {providers.names.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                {providers.unnamed > 0 && (
                  /* a result with nobody against it cannot be queried later */
                  <option value={NO_PROVIDER}>
                    {t("No provider recorded ({n})", { n: providers.unnamed })}
                  </option>
                )}
              </Select>
              <Select
                value={f.outcome ?? ""}
                onChange={(e) => set("outcome", e.target.value || null)}
                aria-label={t("Verdict")}
              >
                <option value="">{t("Any verdict")}</option>
                {OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {t(OUTCOME_LABEL[o])}
                  </option>
                ))}
              </Select>
              <Select
                value={f.framework ?? ""}
                onChange={(e) => set("framework", e.target.value || null)}
                aria-label={t("Framework")}
              >
                <option value="">{t("Any framework")}</option>
                {frameworks.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </Select>
              <Input
                type="date"
                value={f.from ?? ""}
                onChange={(e) => set("from", e.target.value || null)}
                aria-label={t("From")}
              />
              <Input
                type="date"
                value={f.to ?? ""}
                onChange={(e) => set("to", e.target.value || null)}
                aria-label={t("To")}
              />
              {demoCount > 0 && (
                <Select
                  value={f.demo}
                  onChange={(e) => set("demo", e.target.value === "all" ? null : e.target.value)}
                  aria-label={t("Demonstration data")}
                >
                  <option value="all">{t("Real and demonstration")}</option>
                  <option value="real">{t("Real records only")}</option>
                  <option value="demo">{t("Demonstration only")}</option>
                </Select>
              )}
              <span className={s.tileNote}>
                {t("{n} of {total}", { n: rows.length, total: records.data?.length ?? 0 })}
              </span>
            </div>
          )}
          {records.isPending ? (
            <Skeleton rows={6} />
          ) : records.isError ? (
            <ErrorState error={records.error} retry={() => void records.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState>
              {records.data.length ? "No test matches these filters." : "No test has been recorded at this site yet."}
            </EmptyState>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr>
                    <th>{t("Reference")}</th>
                    <th>{t("Test")}</th>
                    <th>{t("Performed")}</th>
                    <th>{t("By")}</th>
                    <th>{t("Verdict")}</th>
                    <th>{t("Results")}</th>
                    <th>{t("Source")}</th>
                    <th>{t("Files · actions")}</th>
                    <th>{t("Next due")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => !reporting && navigate(`/testing-records/${r.id}`)}
                      style={{ cursor: reporting ? undefined : "pointer" }}
                    >
                      <td className="mono">
                        {reporting ? (
                          r.reference
                        ) : (
                          <Link to={`/testing-records/${r.id}`} onClick={(e) => e.stopPropagation()}>
                            {r.reference}
                          </Link>
                        )}
                        {r.revision > 1 && <div className={s.tileNote}>{t("revision {n}", { n: r.revision })}</div>}
                        {r.demo && (
                          <div>
                            <span className={`${s.badge} ${s.demo}`}>{t("DEMO")}</span>
                          </div>
                        )}
                      </td>
                      <td>
                        {r.title}
                        <div className={s.tileNote}>{r.scope ?? r.testTypeName}</div>
                      </td>
                      <td>{formatDate(r.performedOn)}</td>
                      <td>{r.performedBy ?? "—"}</td>
                      <td>
                        <OutcomeText outcome={r.outcome} />
                      </td>
                      <td className={s.note}>
                        {r.results
                          ? t("{n} · {fail} action · {attention} attention", {
                              n: r.results,
                              fail: r.counts.fail ?? 0,
                              attention: r.counts.attention ?? 0,
                            })
                          : r.judged
                            ? t("engineer's verdict")
                            : t("summary")}
                      </td>
                      <td className={s.tileNote}>
                        {t(SOURCE_LABEL[r.source])}
                        {r.sourceFile ? <div className="mono">{r.sourceFile}</div> : null}
                      </td>
                      <td>
                        {r.files || "—"} · {r.actions || "—"}
                      </td>
                      <td>{r.nextDue ? formatDate(r.nextDue) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      <ActivityCard entries={activity.data} loading={activity.isPending} reporting={reporting} />

      {openType && summary.data && (
        <TypeDrawer
          summary={summary.data.types.find((ty) => ty.testType === openType)!}
          type={types.data?.find((ty) => ty.key === openType)}
          records={(records.data ?? []).filter((r) => r.testType === openType)}
          canWrite={canWrite}
          onRecord={() => {
            setOpenType(null);
            set("type", openType);
            setRecording(true);
          }}
          onClose={() => setOpenType(null)}
        />
      )}

      {recording && types.data && (
        <RecordTestDialog
          types={types.data}
          initialType={f.testType ?? undefined}
          onClose={() => setRecording(false)}
          onCreated={(id) => {
            setRecording(false);
            navigate(`/testing-records/${id}`);
          }}
        />
      )}
    </Page>
  );
}

const MODE_EXPLAINED: Record<TestType["mode"], string> = {
  threshold:
    "Each result is judged against the threshold its attributes select; the record is as bad as its worst result.",
  ratio: "A summary figure is judged against the limit it must meet.",
  judged: "The engineer gives the verdict, and must record its basis.",
};

/** A test type opened: what it is, why it is required, what it is judged against and where that comes from, and its history here. */
function TypeDrawer({
  summary: sm,
  type,
  records,
  canWrite,
  onRecord,
  onClose,
}: {
  summary: TypeSummary;
  type?: TestType;
  records: TestRecord[];
  canWrite: boolean;
  onRecord: () => void;
  onClose: () => void;
}) {
  return (
    <Drawer title={sm.name} sub={t(DUE_LABEL[sm.state])} onClose={onClose}>
      {type?.description && (
        <p className={s.note} style={{ margin: 0 }}>
          {type.description}
        </p>
      )}
      <dl className={ds.facts}>
        <Fact label={t("Required by")} source={t("the tenant's test catalog (scripts/seed_test_types.py)")}>
          {sm.frameworks.map((x) => (
            <div key={x}>{x}</div>
          ))}
        </Fact>
        <Fact label={t("How judged")}>{t(MODE_EXPLAINED[sm.mode])}</Fact>
        <Fact
          label={t("Interval")}
          source={
            sm.intervalDays
              ? sm.intervalStatus === "agreed"
                ? t("confirmed")
                : t("proposed — not yet confirmed")
              : undefined
          }
        >
          {sm.intervalDays ? t("every {n} days", { n: sm.intervalDays }) : t("none set")}
        </Fact>
        <Fact label={t("Last test")}>
          {sm.last ? (
            <>
              <Link to={`/testing-records/${sm.last.id}`}>{sm.last.reference}</Link> · {formatDate(sm.last.performedOn)}{" "}
              · <OutcomeText outcome={sm.last.outcome} />
            </>
          ) : (
            t("never tested at this site")
          )}
        </Fact>
        <Fact label={t("Next due")}>{sm.nextDue ? formatDate(sm.nextDue) : "—"}</Fact>
        {type?.measurementKey && (
          <Fact label={t("Feeds")} source={t("results are also written as readings")}>
            {t("the {measurement} measurement, which rules and prediction models read", {
              measurement: type.measurementKey,
            })}
          </Fact>
        )}
      </dl>

      {type && (type.thresholds.length > 0 || type.ratio) && (
        <div className={ds.section}>
          <h3>{t("Judged against")}</h3>
          {type.thresholds.map((th) => (
            <div key={th.label} className={s.note}>
              <b>{th.label}</b>
              {th.match
                ? ` (${Object.entries(th.match)
                    .map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`)
                    .join(", ")})`
                : ""}
              {t(": attention below {attention}, action below {fail}", {
                attention: th.attention ?? "—",
                fail: th.fail ?? "—",
              })}
              {th.basis === "pct_of_specified" ? ` ${t("% of specified")}` : type.unit ? ` ${type.unit}` : ""}{" "}
              <span className={`${s.badge} ${th.status === "agreed" ? "" : s.proposed}`}>
                {th.status === "agreed" ? t("AGREED") : t("PROPOSED")}
              </span>
              {th.basisNote && (
                <div>
                  {t("Source:")} {th.basisNote}
                </div>
              )}
            </div>
          ))}
          {type.ratio && (
            <div className={s.note}>
              <b>{type.ratio.valueLabel}</b> {t("at or above")} <b>{type.ratio.limitLabel}</b>{" "}
              <span className={`${s.badge} ${type.ratio.status === "agreed" ? "" : s.proposed}`}>
                {type.ratio.status === "agreed" ? t("AGREED") : t("PROPOSED")}
              </span>
              {type.ratio.basisNote && (
                <div>
                  {t("Source:")} {type.ratio.basisNote}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* build 6B: the thresholds and interval above, confirmed or changed here */}
      {type && (
        <div className={ds.section}>
          <h3>{t("Thresholds and interval")}</h3>
          <ThresholdsForm type={type} key={type.updatedAt ?? type.key} />
        </div>
      )}

      <div className={ds.section}>
        <h3>{t("Records at this site — {n}", { n: records.length })}</h3>
        {records.length === 0 ? (
          <div className={s.note}>{t("None yet.")}</div>
        ) : (
          <Table>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/testing-records/${r.id}`} onClick={onClose}>
                      {r.reference}
                    </Link>
                    {r.revision > 1 ? ` ${t("rev {n}", { n: r.revision })}` : ""}
                    {r.demo && (
                      <span className={`${s.badge} ${s.demo}`} style={{ marginInlineStart: 6 }}>
                        {t("DEMO")}
                      </span>
                    )}
                  </td>
                  <td>{formatDate(r.performedOn)}</td>
                  <td>
                    <OutcomeText outcome={r.outcome} />
                  </td>
                  <td className={s.tileNote}>
                    {r.results
                      ? t("{fail} action · {attention} attention", {
                          fail: r.counts.fail ?? 0,
                          attention: r.counts.attention ?? 0,
                        })
                      : t(SOURCE_LABEL[r.source])}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      {canWrite && (
        <Button variant="primary" onClick={onRecord}>
          {t("+ Record a {test} test", { test: sm.name.toLowerCase() })}
        </Button>
      )}
    </Drawer>
  );
}

/** The site's testing log: every record made, revised, filed and acted on, newest first. */
function ActivityCard({ entries, loading, reporting }: { entries?: LogEntry[]; loading: boolean; reporting: boolean }) {
  const [kind, setKind] = useState<"all" | "records" | "actions" | "files">("all");
  const [limit, setLimit] = useState(25);
  const shown = (entries ?? []).filter((e) =>
    kind === "all"
      ? true
      : kind === "records"
        ? /created|revised|thresholds_set/.test(e.action)
        : kind === "files"
          ? e.action.endsWith("file_attached")
          : /action_taken|comment|work_order_raised|retest_planned/.test(e.action),
  );
  return (
    <div style={{ marginTop: "var(--gap)" }}>
      <Card
        title="Activity log"
        sub="Every change here, with who and when"
      >
        {!reporting && (
          <div className={s.filters} style={{ marginBottom: 10 }}>
            {(["all", "records", "actions", "files"] as const).map((k) => (
              <Button key={k} size="sm" variant={kind === k ? "primary" : "ghost"} onClick={() => setKind(k)}>
                {k === "all"
                  ? t("Everything")
                  : k === "records"
                    ? t("Records and revisions")
                    : k === "actions"
                      ? t("Actions taken")
                      : t("Files")}
              </Button>
            ))}
          </div>
        )}
        {loading ? (
          <Skeleton rows={4} />
        ) : shown.length === 0 ? (
          <EmptyState>Nothing logged.</EmptyState>
        ) : (
          <>
            <ol className={s.timeline}>
              {shown.slice(0, reporting ? 100 : limit).map((e) => (
                <li key={e.id} className={s.timelineItem} data-action={e.action.split(".").pop()}>
                  <div className={s.timelineHead}>
                    <b>{e.label}</b>
                    {e.recordId && !reporting ? (
                      <Link to={`/testing-records/${e.recordId}`}>
                        {e.reference}
                        {e.revision && e.revision > 1 ? ` ${t("rev {n}", { n: e.revision })}` : ""}
                      </Link>
                    ) : (
                      <span className="mono">{e.reference ?? e.testType}</span>
                    )}
                    <span className={s.tileNote}>
                      {e.by ?? t("no person (seeded)")} · {formatDateTime(e.at)}
                    </span>
                  </div>
                  <div className={s.note}>{describeLog(e)}</div>
                </li>
              ))}
            </ol>
            {!reporting && shown.length > limit && (
              <Button size="sm" onClick={() => setLimit((n) => n + 50)} style={{ marginTop: 8 }}>
                {t("Show more ({n} older)", { n: shown.length - limit })}
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
